"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

export type Language = "en" | "zh";

// Translation keys and their values for both languages
const translations = {
  // TopBar
  "topBar.shortcuts": { en: "Shortcuts", zh: "快捷键" },
  "topBar.view": { en: "View", zh: "预览" },
  
  // Panel Wizard
  "wizard.script": { en: "Script", zh: "剧本" },
  "wizard.scriptPlaceholder": { en: "Paste script here...", zh: "可输入中英剧本，生成的提示词都是中文" },
  "wizard.characters": { en: "Characters", zh: "角色" },
  "wizard.outfits": { en: "Outfits", zh: "服装" },
  "wizard.background": { en: "Background", zh: "背景" },
  "wizard.backgroundPlaceholder": { en: "Background description (optional)", zh: "背景描述（可选）" },
  "wizard.generatePrompts": { en: "Generate Prompts", zh: "生成提示词" },
  "wizard.processing": { en: "Processing...", zh: "处理中..." },
  "wizard.panelPrompts": { en: "Panel Prompts", zh: "分镜提示词" },
  "wizard.panel": { en: "Panel", zh: "分镜" },
  "wizard.panels": { en: "Panels", zh: "分镜数" },
  "wizard.addBubbles": { en: "Add bubbles", zh: "添加对话框" },
  "wizard.batchGenerate": { en: "Batch Generate", zh: "批量生成" },
  "wizard.generating": { en: "Generating", zh: "生成中" },
  "wizard.starting": { en: "Starting...", zh: "启动中..." },
  "wizard.addCharacter": { en: "+ Add Character", zh: "+ 添加角色" },
  "wizard.name": { en: "Name", zh: "名称" },
  "wizard.outfitPlaceholder": { en: "Outfit description...", zh: "服装描述..." },
  
  // Panel Editor (Right Panel)
  "editor.aiModel": { en: "AI Model", zh: "AI模型" },
  "editor.v1SD15": { en: "v1 (SD 1.5/Flux)", zh: "v1 (SD 1.5/Flux)" },
  "editor.v2Seedream": { en: "v2 (Seedream)", zh: "v2 (Seedream)" },
  "editor.character": { en: "Character", zh: "角色" },
  "editor.prompt": { en: "Prompt", zh: "提示词" },
  "editor.negativePrompt": { en: "Negative Prompt", zh: "负向提示词" },
  "editor.baseModel": { en: "Base Model", zh: "基础模型" },
  "editor.variationStrength": { en: "Variation Strength", zh: "变化强度" },
  "editor.redraw": { en: "Redraw", zh: "重绘" },
  "editor.upscale": { en: "Upscale", zh: "放大" },
  "editor.upscale4k": { en: "4K Upscale", zh: "4K放大" },
  "editor.upscaling": { en: "Upscaling...", zh: "放大中..." },
  "editor.noImageToUpscale": { en: "No image to upscale", zh: "没有图片可放大" },
  "editor.upscaleSuccess": { en: "Image upscaled successfully", zh: "图片放大成功" },
  "editor.describeWhatYouWant": { en: "Describe what you want", zh: "描述你想要的效果" },
  "editor.editMode": { en: "Edit mode", zh: "编辑模式" },
  "editor.generateNew": { en: "Generate new", zh: "生成新图" },
  "editor.promptPlaceholder": { en: "e.g., 'Change camera angle' or 'Generate the next scene'", zh: "例如：'改变镜头角度' 或 '生成下一个场景'" },
  "editor.references": { en: "References", zh: "参考图" },
  "editor.searchCharacter": { en: "Search character...", zh: "搜索角色..." },
  "editor.recent": { en: "Recent", zh: "最近使用" },
  "editor.count": { en: "Count", zh: "数量" },
  "editor.generate": { en: "Generate", zh: "生成" },
  "editor.edit": { en: "Edit", zh: "编辑" },
  "editor.editImage": { en: "Edit Image", zh: "编辑图片" },
  "editor.editImages": { en: "Edit {count} images", zh: "编辑 {count} 张图片" },
  "editor.generateNewImages": { en: "Generate {count} new", zh: "生成 {count} 张新图" },
  "editor.editing": { en: "Editing...", zh: "编辑中..." },
  "editor.generating": { en: "Generating...", zh: "生成中..." },
  "editor.loading": { en: "Loading...", zh: "加载中..." },
  "editor.generationHistory": { en: "Generation History", zh: "生成历史" },
  "editor.openDrawingPad": { en: "Open Drawing Pad", zh: "打开绘画板" },
  "editor.closeDrawingPad": { en: "Close Drawing Pad", zh: "关闭绘画板" },
  "editor.pasteImage": { en: "Paste Image", zh: "粘贴图片" },
  
  // Left Panel Tabs
  "leftPanel.panels": { en: "Panels", zh: "分镜" },
  "leftPanel.comments": { en: "Comments", zh: "反馈" },
  
  // Right Panel Blank State
  "rightPanel.comicEditor": { en: "Comic Editor", zh: "漫画编辑器" },
  "rightPanel.selectPanel": { en: "Select a panel or add new elements", zh: "选择一个分镜或添加新元素" },
  "rightPanel.addPanel": { en: "Add Panel", zh: "添加分镜" },
  "rightPanel.addPanelDesc": { en: "Create a new image panel", zh: "创建一个新的图像分镜" },
  "rightPanel.addBubble": { en: "Add Bubble", zh: "添加气泡" },
  "rightPanel.addBubbleDesc": { en: "Add speech or thought bubble", zh: "添加对话或思想气泡" },
  "rightPanel.addComment": { en: "Add Comment", zh: "添加反馈" },
  "rightPanel.addCommentDesc": { en: "Leave a note or feedback", zh: "留下备注或反馈" },
  
  // Common
  "common.cancel": { en: "Cancel", zh: "取消" },
  "common.save": { en: "Save", zh: "保存" },
  "common.delete": { en: "Delete", zh: "删除" },
  "common.copy": { en: "Copy", zh: "复制" },
  "common.paste": { en: "Paste", zh: "粘贴" },
  "common.undo": { en: "Undo", zh: "撤销" },
  "common.redo": { en: "Redo", zh: "重做" },
} as const;

type TranslationKey = keyof typeof translations;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

const STORAGE_KEY = "comic-creator-language";

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>("en");
  const [isInitialized, setIsInitialized] = useState(false);

  // Load language from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem(STORAGE_KEY) as Language | null;
    if (savedLanguage && (savedLanguage === "en" || savedLanguage === "zh")) {
      setLanguageState(savedLanguage);
    }
    setIsInitialized(true);
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  }, []);

  const toggleLanguage = useCallback(() => {
    const newLang = language === "en" ? "zh" : "en";
    setLanguage(newLang);
  }, [language, setLanguage]);

  const t = useCallback((key: TranslationKey, params?: Record<string, string | number>): string => {
    const translation = translations[key]?.[language] || key;
    
    if (params) {
      return Object.entries(params).reduce<string>(
        (str, [param, value]) => str.replace(`{${param}}`, String(value)),
        translation
      );
    }
    
    return translation;
  }, [language]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    toggleLanguage,
    t,
  }), [language, setLanguage, toggleLanguage, t]);

  // Prevent hydration mismatch by not rendering until initialized
  if (!isInitialized) {
    return null;
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

// Export translations type for type safety
export type { TranslationKey };

